import React, { useState, Suspense, lazy } from 'react';
import { number, string, func, bool } from 'prop-types';

import { Rating } from '@thd-olt-component-react/rating';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { QueryProvider } from '@thd-nucleus/data-sources';

import { noop } from '../../../../utils/promo-utils';
import { PromoSidecar } from '../../../promo-sidecar/PromoSidecar';
import { PromoQuickviewSectionCta } from '../promo-quickview-section-cta/PromoQuickviewSectionCta';
import '../../PromoQuickview.scss';
import './PromoQuickviewReviews.scss';

// lazy-load ratings and reviews (comments signal webpack to code-split)
const RatingsAndReviews = lazy(() => import(
  /* webpackPrefetch: true */
  /* webpackChunkName: "ratings-and-reviews" */
  '@thd-olt-component-react/ratings-and-reviews'
).then((mod) => {
  return {
    default: (props) => {
      const { RatingsAndReviews: RatingsAndReviewsComp } = mod;

      return (
        <QueryProvider>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <RatingsAndReviewsComp {...props} />
        </QueryProvider>
      );
    }
  };
}));

const SuspenseFallback = () => (
  <div className="promo-quickview-reviews-ratings__suspense-fallback">
    {(new Array(6)).fill(0).map((item, index) => (
      <LoadingPlaceholder
        key={`loading-placeholder-${index}`}
        type="media"
        color="#E0E0E0"
        rows={6}
        ready={false}
        showLoadingAnimation
        style={{
          marginBottom: '20px'
        }}
      >
        <div />
      </LoadingPlaceholder>
    ))}
  </div>
);

export const PromoQuickviewReviews = ({
  itemId,
  averageRating,
  totalRatings,
  onSectionClick,
  selected,
  onSelect,
  setQuantityWarningText,
  showQuantityWarningState
}) => {
  const [renderReviews, setRenderReviews] = useState(false);
  const [isReviewsOpen, setIsReviewsOpen] = useState(false);

  return (
    <>
      <PromoQuickviewSectionCta
        setOpen={setIsReviewsOpen}
        setRender={setRenderReviews}
        clickAnalytics={onSectionClick}
        sectionName="Reviews"
      >
        <div className="promo-quickview-reviews-ratings">
          <Rating value={averageRating} />
          <span>({totalRatings})</span>
        </div>
        <div className="promo-quickview-section--fill" />
      </PromoQuickviewSectionCta>

      {renderReviews && (
        <PromoSidecar
          isSidecarOpen={isReviewsOpen}
          setIsSidecarOpen={setIsReviewsOpen}
          zIndex={1000}
          backText="Back to Quick View"
          itemId={itemId}
          useSidecarNav
          useSidecarCta
          selected={selected}
          onSelect={onSelect}
          setQuantityWarningText={setQuantityWarningText}
          showQuantityWarningState={showQuantityWarningState}
        >
          <article className="promo-quickview-section-sidecar">
            <Suspense fallback={<SuspenseFallback />}>
              <RatingsAndReviews itemId={itemId} forceCondensedLayout />
            </Suspense>
          </article>
        </PromoSidecar>
      )}
    </>
  );
};

PromoQuickviewReviews.displayName = 'PromoQuickviewReviews';

PromoQuickviewReviews.propTypes = {
  averageRating: number.isRequired,
  totalRatings: string.isRequired,
  itemId: string.isRequired,
  onSectionClick: func,
  selected: bool.isRequired,
  onSelect: func.isRequired,
  setQuantityWarningText: func,
  showQuantityWarningState: bool
};

PromoQuickviewReviews.defaultProps = {
  onSectionClick: noop,
  setQuantityWarningText: noop,
  showQuantityWarningState: false
};
